import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Switch,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { hipaaAuthorizationService } from '../../../../services/hipaaAuthorizationInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('HIPAAAuthorizationInput');

type HIPAAAuthorizationInputScreenRouteProp = RouteProp<RootStackParamList, 'HIPAAAuthorizationInput'>;

export const HIPAAAuthorizationInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<HIPAAAuthorizationInputScreenRouteProp>();

  const { hipaaAuthorizationData, isEditing } = route.params || {};

  const [authorizedPerson1Name, setAuthorizedPerson1Name] = useState(
    hipaaAuthorizationData?.authorizedPersons?.[0]?.name || ''
  );
  const [authorizedPerson1Relationship, setAuthorizedPerson1Relationship] = useState(
    hipaaAuthorizationData?.authorizedPersons?.[0]?.relationship || ''
  );
  const [authorizedPerson1Email, setAuthorizedPerson1Email] = useState(
    hipaaAuthorizationData?.authorizedPersons?.[0]?.email || ''
  );

  const [authorizedPerson2Name, setAuthorizedPerson2Name] = useState(
    hipaaAuthorizationData?.authorizedPersons?.[1]?.name || ''
  );
  const [authorizedPerson2Relationship, setAuthorizedPerson2Relationship] = useState(
    hipaaAuthorizationData?.authorizedPersons?.[1]?.relationship || ''
  );
  const [authorizedPerson2Email, setAuthorizedPerson2Email] = useState(
    hipaaAuthorizationData?.authorizedPersons?.[1]?.email || ''
  );

  const [healthcareProvider1Name, setHealthcareProvider1Name] = useState(
    hipaaAuthorizationData?.healthcareProviders?.[0]?.name || ''
  );
  const [healthcareProvider1Type, setHealthcareProvider1Type] = useState(
    hipaaAuthorizationData?.healthcareProviders?.[0]?.type || ''
  );

  const [additionalRestrictions, setAdditionalRestrictions] = useState(
    hipaaAuthorizationData?.additionalRestrictions || ''
  );
  const [authorizationDate, setAuthorizationDate] = useState(
    hipaaAuthorizationData?.authorizationDate || ''
  );
  const [hasExpiration, setHasExpiration] = useState(
    hipaaAuthorizationData?.expirationDate && hipaaAuthorizationData.expirationDate !== 'No Expiration' ? true : false
  );
  const [expirationDate, setExpirationDate] = useState(
    hipaaAuthorizationData?.expirationDate && hipaaAuthorizationData.expirationDate !== 'No Expiration'
      ? hipaaAuthorizationData.expirationDate
      : ''
  );
  const [saving, setSaving] = useState(false);

  const isValidEmail = (email: string) => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSave = async () => {
    if (!authorizedPerson1Name.trim()) {
      Alert.alert('Error', 'Please enter at least one authorized person name');
      return;
    }

    if (!authorizedPerson1Relationship.trim()) {
      Alert.alert('Error', 'Please enter the relationship for the authorized person');
      return;
    }

    if (authorizedPerson1Email && !isValidEmail(authorizedPerson1Email)) {
      Alert.alert('Error', 'Please enter a valid email address');
      return;
    }

    try {
      setSaving(true);

      const authorizedPersons = [
        { name: authorizedPerson1Name.trim(), relationship: authorizedPerson1Relationship.trim(), email: authorizedPerson1Email.trim() }
      ];
      if (authorizedPerson2Name.trim()) {
        authorizedPersons.push({ name: authorizedPerson2Name.trim(), relationship: authorizedPerson2Relationship.trim(), email: authorizedPerson2Email.trim() });
      }

      const healthcareProviders = [];
      if (healthcareProvider1Name.trim()) {
        healthcareProviders.push({ name: healthcareProvider1Name.trim(), type: healthcareProvider1Type.trim() });
      }

      const hipaaData = {
        authorizedPersons,
        healthcareProviders,
        additionalRestrictions: additionalRestrictions.trim(),
        authorizationDate: authorizationDate.trim(),
        expirationDate: hasExpiration ? expirationDate.trim() : 'No Expiration',
      };

      let savedData;
      if (isEditing && hipaaAuthorizationData?.id) {
        savedData = await hipaaAuthorizationService.updateHIPAAAuthorization(hipaaAuthorizationData.id, hipaaData);
        const updatedData = savedData?.data || savedData;
        logger.debug('HIPAA Authorization updated successfully with ID:', updatedData.id);
        navigation.navigate('HIPAAAuthorizationLockUnlock', { hipaaAuthorizationId: updatedData.id });
      } else {
        savedData = await hipaaAuthorizationService.createHIPAAAuthorization(hipaaData);
        const createdData = savedData?.data || savedData;
        logger.debug('HIPAA Authorization saved successfully with ID:', createdData.id);
        navigation.navigate('HIPAAAuthorizationLockUnlock', { hipaaAuthorizationId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving HIPAA authorization:', error);
      Alert.alert('Error', 'Failed to save HIPAA authorization. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Add Your HIPAA Authorization</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <Text style={styles.sectionTitle}>Authorized Person 1:</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Full Name:</Text>
            <TextInput
              style={styles.input}
              value={authorizedPerson1Name}
              onChangeText={setAuthorizedPerson1Name}
              placeholder="Enter full name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Relationship:</Text>
            <TextInput
              style={styles.input}
              value={authorizedPerson1Relationship}
              onChangeText={setAuthorizedPerson1Relationship}
              placeholder="e.g., Spouse, Adult Child"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email (Optional):</Text>
            <TextInput
              style={styles.input}
              value={authorizedPerson1Email}
              onChangeText={setAuthorizedPerson1Email}
              placeholder="Enter email address"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <Text style={styles.sectionTitle}>Authorized Person 2 (Optional):</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Full Name:</Text>
            <TextInput
              style={styles.input}
              value={authorizedPerson2Name}
              onChangeText={setAuthorizedPerson2Name}
              placeholder="Enter full name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Relationship:</Text>
            <TextInput
              style={styles.input}
              value={authorizedPerson2Relationship}
              onChangeText={setAuthorizedPerson2Relationship}
              placeholder="e.g., Spouse, Adult Child"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email (Optional):</Text>
            <TextInput
              style={styles.input}
              value={authorizedPerson2Email}
              onChangeText={setAuthorizedPerson2Email}
              placeholder="Enter email address"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <Text style={styles.sectionTitle}>Healthcare Provider (Optional):</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Provider Name:</Text>
            <TextInput
              style={styles.input}
              value={healthcareProvider1Name}
              onChangeText={setHealthcareProvider1Name}
              placeholder="Enter provider name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Provider Type:</Text>
            <TextInput
              style={styles.input}
              value={healthcareProvider1Type}
              onChangeText={setHealthcareProvider1Type}
              placeholder="e.g., Hospital, Clinic"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Authorization Date:</Text>
            <TextInput
              style={styles.input}
              value={authorizationDate}
              onChangeText={setAuthorizationDate}
              placeholder="MM/DD/YYYY"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.toggleRow}>
              <Text style={styles.label}>Has Expiration Date:</Text>
              <Switch
                value={hasExpiration}
                onValueChange={setHasExpiration}
                trackColor={{ false: '#d8dbdd', true: '#29BFC0' }}
                thumbColor={hasExpiration ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>
          </View>

          {hasExpiration && (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Expiration Date:</Text>
              <TextInput
                style={styles.input}
                value={expirationDate}
                onChangeText={setExpirationDate}
                placeholder="MM/DD/YYYY"
                placeholderTextColor="#A9A9A9"
              />
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Restrictions (Optional):</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={additionalRestrictions}
              onChangeText={setAdditionalRestrictions}
              placeholder="Enter any restrictions or limitations"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(16),
    marginTop: ms(10),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  toggleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    minHeight: s(80),
    textAlignVertical: 'top',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});
